/* eslint-disable react/require-default-props */
import React, {memo, Suspense, useEffect, useState, startTransition} from "react"
import {MANIFEST} from "@manifest/header"
import {useTemplatedComponent} from "@next/templating"
import {Label, ShoppingBagLink} from "./components"
import {HEADER_NAV_BAR_QUICK_LINKS, HEADER_NAV_BAR_QUICK_LINKS_SHOPPING_BAG} from "../../config/constants"
import connect from "./connect"

type ShoppingBagIconProps = {
    shoppingBagUrl: string
    onClick: (data: any) => void
    testId: string
    items: number | string
    iconUrl: string
    altText: string
    itemsInBadge?: boolean
    isBagVisible: boolean
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
}

const ItemsCountInner = memo(
    ({
        items,
        itemsInBadge,
        children,
        templating,
    }: Pick<ShoppingBagIconProps, "items" | "itemsInBadge" | "templating"> & {children: React.ReactNode}) => {
        const [isHydrated, setIsHydrated] = useState(false)
        const TemplatedShoppingBagBadge = useTemplatedComponent(templating, MANIFEST)

        // Track hydration completion
        useEffect(() => {
            startTransition(() => {
                setIsHydrated(true)
            })
        }, [])

        // Always render the same structure to avoid hydration mismatches
        // The content inside should be determined by the current state
        if (!isHydrated) {
            // During SSR and initial hydration, render children only
            return children as React.ReactElement
        }

        if (itemsInBadge) {
            // After hydration, render templated badge if enabled
            return <TemplatedShoppingBagBadge items={items}>{children}</TemplatedShoppingBagBadge>
        }

        // After hydration, render label + children if badge disabled
        return (
            <>
                <Label>{items}</Label>
                {children}
            </>
        )
    },
)
ItemsCountInner.displayName = "ItemsCountInner"

const ItemsCount = memo(
    (props: Pick<ShoppingBagIconProps, "items" | "itemsInBadge" | "templating"> & {children: React.ReactNode}) => (
        <Suspense>
            <ItemsCountInner {...props} />
        </Suspense>
    ),
)
ItemsCount.displayName = "ItemsCount"

const ShoppingBagIcon = memo(
    ({
        shoppingBagUrl,
        onClick,
        testId,
        items,
        iconUrl,
        altText,
        itemsInBadge,
        isBagVisible,
        templating,
    }: ShoppingBagIconProps) => {
        return (
            <ShoppingBagLink
                data-testid={testId}
                onClick={onClick}
                href={shoppingBagUrl}
                data-ga-v1={HEADER_NAV_BAR_QUICK_LINKS}
                data-ga-v2={HEADER_NAV_BAR_QUICK_LINKS_SHOPPING_BAG}
                aria-label={HEADER_NAV_BAR_QUICK_LINKS_SHOPPING_BAG}
                title={HEADER_NAV_BAR_QUICK_LINKS_SHOPPING_BAG}
                rel="nofollow"
                aria-expanded={isBagVisible}
            >
                <ItemsCount items={items} itemsInBadge={itemsInBadge} templating={templating}>
                    <img src={iconUrl} alt={altText} aria-hidden />
                </ItemsCount>
            </ShoppingBagLink>
        )
    },
)
ShoppingBagIcon.displayName = "ShoppingBagIcon"

export default connect(ShoppingBagIcon)
